import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Search, User, Heart, ShoppingCart, Menu, X, ChevronDown,
  LayoutGrid, Zap, Star, Sparkles, Flame, Calculator, Users, Package,
  Headphones, Camera, Shirt, Home, Gamepad2, Smartphone, Pencil,
  Dumbbell, Car, PawPrint, MoreHorizontal, ArrowLeft, Plane, Ship,
} from "lucide-react";
import { useStoreSettings } from "@/hooks/useStoreSettings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
let _hpCache = null;

// ── Icon map ──────────────────────────────────────────────────────────────────
const ICON_MAP = {
  LayoutGrid, Zap, Star, Sparkles, Flame, Calculator, Users, Package,
  Headphones, Shirt, Home, Gamepad2, Smartphone, Pencil, Dumbbell,
  Car, PawPrint, MoreHorizontal, Plane, Ship, Heart, Search,
};

function NavIcon({ name, url, className = "w-4 h-4" }) {
  if (url) return <img src={url} alt="" className={`${className} object-contain`} />;
  const Comp = ICON_MAP[name];
  return Comp ? <Comp className={className} /> : null;
}

// ── Fallback defaults (before config loads) ───────────────────────────────────
const DEFAULT_HEADER = {
  search: { enabled: true, placeholder: "Search for Products, Brands and more..." },
  cta_button: { enabled: true, text: "Import from China", url: "/pages/import", icon1_url: "", icon2_url: "" },
  account: { enabled: true },
  wishlist: { enabled: true },
  cart: { enabled: true },
  nav_items: [
    { id: "all-categories", type: "mega-menu", label: "All Categories", enabled: true, icon_name: "LayoutGrid", icon_url: "", highlight: true },
    { id: "deals", label: "Deals", url: "/search?sort=discount", enabled: true, icon_name: "Zap", icon_url: "", badge: "" },
    { id: "bestsellers", label: "Bestsellers", url: "/search?q=bestsellers", enabled: true, icon_name: "Star", icon_url: "" },
    { id: "new-arrivals", label: "New Arrivals", url: "/search?q=new", enabled: true, icon_name: "Sparkles", icon_url: "", badge: "NEW", badge_color: "#2563eb" },
    { id: "trending", label: "Trending", url: "/search?q=trending", enabled: true, icon_name: "Flame", icon_url: "" },
    { id: "import-calculator", label: "Import Calculator", url: "/pages/import", enabled: true, icon_name: "Calculator", icon_url: "" },
    { id: "become-reseller", label: "Become Reseller", url: "/become-reseller", enabled: true, icon_name: "Users", icon_url: "" },
    { id: "track-order", label: "Track Order", url: "/track-order", enabled: true, icon_name: "Package", icon_url: "", separator_before: true },
    { id: "support", label: "Support", url: "/support", enabled: true, icon_name: "Headphones", icon_url: "" },
  ],
};

const DEFAULT_MEGA = {
  enabled: true,
  categories: [
    { id: "fashion", label: "Fashion & Apparel", icon_name: "Shirt", icon_url: "", url: "/c/fashion-apparel", enabled: true, sub_groups: [] },
    { id: "beauty", label: "Beauty & Personal Care", icon_name: "Sparkles", icon_url: "", url: "/c/beauty", enabled: true, sub_groups: [] },
    { id: "home", label: "Home & Kitchen", icon_name: "Home", icon_url: "", url: "/c/home-kitchen", enabled: true, sub_groups: [] },
    { id: "toys", label: "Toys & Games", icon_name: "Gamepad2", icon_url: "", url: "/c/toys-games", enabled: true, sub_groups: [] },
    { id: "electronics", label: "Electronics & Accessories", icon_name: "Zap", icon_url: "", url: "/c/electronics", enabled: true, sub_groups: [] },
    { id: "mobile", label: "Mobile & Accessories", icon_name: "Smartphone", icon_url: "", url: "/c/mobile", enabled: true, sub_groups: [] },
    { id: "office", label: "Office & Stationery", icon_name: "Pencil", icon_url: "", url: "/c/office-stationery", enabled: true, sub_groups: [] },
    { id: "sports", label: "Sports & Fitness", icon_name: "Dumbbell", icon_url: "", url: "/c/sports-fitness", enabled: true, sub_groups: [] },
    { id: "automotive", label: "Automotive & Bike", icon_name: "Car", icon_url: "", url: "/c/automotive", enabled: true, sub_groups: [] },
    { id: "pets", label: "Pet Supplies", icon_name: "PawPrint", icon_url: "", url: "/c/pet-supplies", enabled: true, sub_groups: [] },
  ],
  creative: { enabled: true, type: "image", url: "", title: "Shop the Latest", subtitle: "Explore new arrivals", cta_text: "Shop Now →", cta_url: "" },
  shop_by_category: { enabled: false, title: "Shop by Category", items: [] },
};

const getUser = () => {
  try { return JSON.parse(localStorage.getItem("it_customer_user") || "null"); } catch { return null; }
};

export default function SiteHeader({
  cartCount = 0,
  cartTotal = 0,
  wishlistCount = 0,
  showNav = true,
  defaultSearch = "",
}) {
  const navigate = useNavigate();
  const [q, setQ] = useState(defaultSearch);
  const [hpConfig, setHpConfig] = useState(null);
  const [megaOpen, setMegaOpen] = useState(false);
  const [activeCatId, setActiveCatId] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCatId, setMobileCatId] = useState(null);
  const closeTimer = useRef(null);
  const megaRef = useRef(null);
  const headerRef = useRef(null);
  const [megaTop, setMegaTop] = useState(116);
  const user = getUser();
  const storeSettings = useStoreSettings();
  const logoUrl = storeSettings?.logo_url;
  const brandColor = storeSettings?.brand_color || "#f97316";

  useEffect(() => {
    if (_hpCache) { setHpConfig(_hpCache); return; }
    axios.get(`${API}/homepage`).then(r => { _hpCache = r.data; setHpConfig(r.data); }).catch(() => {});
  }, []);

  const headerCfg = hpConfig?.header || DEFAULT_HEADER;
  const megaCfg = hpConfig?.mega_menu || DEFAULT_MEGA;
  const navItems = (headerCfg.nav_items || DEFAULT_HEADER.nav_items).filter(i => i.enabled !== false);
  const enabledCats = (megaCfg.categories || []).filter(c => c.enabled !== false);
  const activeCat = enabledCats.find(c => c.id === activeCatId) || enabledCats[0];

  const handleSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  // ── Mega menu hover logic ──
  const openMega = useCallback(() => {
    clearTimeout(closeTimer.current);
    setMegaOpen(true);
    if (!activeCatId && enabledCats.length) setActiveCatId(enabledCats[0].id);
  }, [activeCatId, enabledCats]);

  const closeMega = useCallback(() => {
    closeTimer.current = setTimeout(() => setMegaOpen(false), 180);
  }, []);

  const keepOpen = useCallback(() => {
    clearTimeout(closeTimer.current);
  }, []);

  // close mega on route change / ESC
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { setMegaOpen(false); setMobileOpen(false); } };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Track header bottom for mega menu positioning
  useEffect(() => {
    const update = () => {
      if (headerRef.current) {
        setMegaTop(headerRef.current.getBoundingClientRect().bottom);
      }
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update, { passive: true });
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  const ctaCfg = headerCfg.cta_button || DEFAULT_HEADER.cta_button;
  const searchCfg = headerCfg.search || DEFAULT_HEADER.search;

  const mobileShortcuts = navItems.filter(i => i.type !== "mega-menu");

  return (
    <>
      {/* ══════════════════════════════════════════
          DESKTOP HEADER ROW
      ══════════════════════════════════════════ */}
      <header ref={headerRef} className="bg-[#0d0d1a] sticky top-0 z-40" data-testid="site-header">
        {/* Desktop top row */}
        <div className="hidden md:flex max-w-[1800px] mx-auto px-4 h-[72px] items-center gap-4">

          {/* Logo */}
          <Link to="/" className="flex-shrink-0" data-testid="site-logo">
            {logoUrl ? (
              <img src={logoUrl} alt="Store logo" className="h-12 w-auto max-w-[180px] object-contain" />
            ) : (
              <div className="flex flex-col leading-none items-start">
                <span className="text-2xl font-black tracking-tight leading-none" style={{ color: brandColor }}>
                  IMPORT<span className="text-white">REND</span>
                </span>
                <span className="text-[9px] text-slate-400 font-bold tracking-[0.22em] mt-0.5 self-end">
                  GLOBAL DEALS ——
                </span>
              </div>
            )}
          </Link>

          {/* Search bar */}
          {searchCfg.enabled !== false && (
            <form onSubmit={handleSearch} className="flex-1 max-w-[700px]">
              <div className="flex h-11 rounded-full overflow-hidden bg-white border border-slate-200 shadow-sm">
                <div className="flex items-center pl-4 pr-2 text-slate-400">
                  <Search className="w-4 h-4" />
                </div>
                <input
                  data-testid="site-search-input"
                  value={q}
                  onChange={e => setQ(e.target.value)}
                  placeholder={searchCfg.placeholder || "Search for Products, Brands and more..."}
                  className="flex-1 text-sm text-slate-700 placeholder-slate-400 outline-none bg-transparent"
                />
                <button type="button" className="px-4 text-slate-400 hover:text-slate-600 transition-colors">
                  <Camera className="w-4 h-4" />
                </button>
              </div>
            </form>
          )}

          {/* CTA Button */}
          {ctaCfg.enabled !== false && (
            <Link
              to={ctaCfg.url || "/pages/import"}
              className="flex-shrink-0 flex items-center gap-2 px-5 h-11 rounded-full font-bold text-white text-sm transition-all hover:brightness-110 shadow-lg"
              style={{ backgroundColor: brandColor }}
              data-testid="header-cta-btn"
            >
              {ctaCfg.icon1_url
                ? <img src={ctaCfg.icon1_url} alt="" className="w-5 h-5 object-contain" />
                : <Plane className="w-4 h-4" />
              }
              {ctaCfg.icon2_url
                ? <img src={ctaCfg.icon2_url} alt="" className="w-5 h-5 object-contain" />
                : <Ship className="w-4 h-4" />
              }
              <span>{ctaCfg.text}</span>
              <span className="opacity-80">›</span>
            </Link>
          )}

          {/* Right section: Account | Wishlist | Cart */}
          <div className="flex items-stretch divide-x divide-slate-700 ml-auto flex-shrink-0">
            {/* Account */}
            {headerCfg.account?.enabled !== false && (
              <Link to={user ? "/account" : "/customer-login"} data-testid="site-account-btn"
                className="flex items-center gap-2.5 px-4 text-white hover:bg-white/5 transition-colors">
                <User className="w-6 h-6 text-slate-300 flex-shrink-0" />
                <div className="text-left">
                  <p className="text-[10px] text-slate-400 leading-tight">{user ? `Hello, ${user.name?.split(" ")[0]}` : "Hello, Sign In"}</p>
                  <p className="text-xs font-semibold leading-tight flex items-center gap-0.5">
                    Account <ChevronDown className="w-3 h-3" />
                  </p>
                </div>
              </Link>
            )}

            {/* Wishlist */}
            {headerCfg.wishlist?.enabled !== false && (
              <Link to="/wishlist" data-testid="site-wishlist-btn"
                className="flex items-center gap-2 px-4 text-white hover:bg-white/5 transition-colors">
                <Heart className={`w-6 h-6 flex-shrink-0 ${wishlistCount > 0 ? "fill-red-500 text-red-500" : "text-slate-300"}`} />
                <div className="text-left hidden lg:block">
                  <p className="text-[10px] text-slate-400 leading-tight">{wishlistCount > 0 ? `${wishlistCount} items` : "Saved"}</p>
                  <p className="text-xs font-semibold leading-tight">Wishlist</p>
                </div>
              </Link>
            )}

            {/* Cart */}
            {headerCfg.cart?.enabled !== false && (
              <Link to="/cart" data-testid="site-cart-btn"
                className="flex items-center gap-2 px-4 text-white hover:bg-white/5 transition-colors">
                <div className="relative flex-shrink-0">
                  <ShoppingCart className="w-6 h-6 text-slate-300" />
                  {cartCount > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 text-white text-[8px] font-bold w-4 h-4 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: brandColor }}>
                      {cartCount > 9 ? "9+" : cartCount}
                    </span>
                  )}
                </div>
                <div className="text-left hidden lg:block">
                  <p className="text-[10px] text-slate-400 leading-tight">₹{Number(cartTotal || 0).toLocaleString("en-IN")}</p>
                  <p className="text-xs font-semibold leading-tight">Cart</p>
                </div>
              </Link>
            )}
          </div>
        </div>

        {/* ══════════════════════════════════════════
            MOBILE HEADER
        ══════════════════════════════════════════ */}
        <div className="md:hidden">
          {/* Row 1: Hamburger | Logo | Heart + Cart + Account */}
          <div className="flex items-center px-3 h-14 gap-2">
            <button
              data-testid="mobile-menu-btn"
              onClick={() => { setMobileOpen(v => !v); setMobileCatId(null); }}
              className="text-slate-300 hover:text-white p-1 flex-shrink-0">
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>

            <Link to="/" className="flex-1 flex justify-center">
              {logoUrl ? (
                <img src={logoUrl} alt="Store logo" className="h-8 max-w-[140px] object-contain" />
              ) : (
                <div className="flex flex-col leading-none items-center">
                  <span className="text-lg font-black tracking-tight leading-none" style={{ color: brandColor }}>
                    IMPORT<span className="text-white">REND</span>
                  </span>
                  <span className="text-[7px] text-slate-400 font-bold tracking-[0.2em]">GLOBAL DEALS ——</span>
                </div>
              )}
            </Link>

            <div className="flex items-center gap-3 flex-shrink-0">
              {headerCfg.wishlist?.enabled !== false && (
                <Link to="/wishlist" className="relative">
                  <Heart className={`w-5 h-5 ${wishlistCount > 0 ? "fill-red-500 text-red-500" : "text-slate-300"}`} />
                </Link>
              )}
              {headerCfg.cart?.enabled !== false && (
                <Link to="/cart" className="relative">
                  <ShoppingCart className="w-5 h-5 text-slate-300" />
                  {cartCount > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 text-white text-[8px] font-bold w-4 h-4 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: brandColor }}>
                      {cartCount > 9 ? "9+" : cartCount}
                    </span>
                  )}
                </Link>
              )}
              {headerCfg.account?.enabled !== false && (
                <Link to={user ? "/account" : "/customer-login"}>
                  <User className="w-5 h-5 text-slate-300" />
                </Link>
              )}
            </div>
          </div>

          {/* Row 2: Search */}
          {searchCfg.enabled !== false && (
            <div className="px-3 pb-2">
              <form onSubmit={handleSearch}>
                <div className="flex h-10 rounded-full overflow-hidden bg-white border border-slate-200">
                  <div className="flex items-center pl-3 text-slate-400">
                    <Search className="w-4 h-4" />
                  </div>
                  <input
                    value={q}
                    onChange={e => setQ(e.target.value)}
                    placeholder={searchCfg.placeholder || "Search for Products, Brands and more..."}
                    className="flex-1 px-2 text-sm text-slate-700 placeholder-slate-400 outline-none bg-transparent"
                  />
                  <button type="button" className="px-3 text-slate-400">
                    <Camera className="w-4 h-4" />
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Row 3: Shortcuts horizontal scroll */}
          {showNav && mobileShortcuts.length > 0 && (
            <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide px-3 pb-2.5" style={{ scrollbarWidth: "none" }}>
              {mobileShortcuts.map((item, idx) => (
                <Link
                  key={item.id || idx}
                  to={item.url || "/"}
                  className="flex-shrink-0 flex flex-col items-center gap-1 px-3 py-1.5 rounded-lg min-w-[64px] transition-colors hover:bg-white/10"
                  style={{ borderBottom: idx === 0 ? `2px solid ${brandColor}` : "2px solid transparent" }}
                >
                  <span className={idx === 0 ? "" : "text-slate-400"} style={idx === 0 ? { color: brandColor } : {}}>
                    <NavIcon name={item.icon_name} url={item.icon_url} className="w-5 h-5" />
                  </span>
                  <span className={`text-[9px] font-semibold leading-tight text-center whitespace-nowrap ${idx === 0 ? "text-white" : "text-slate-400"}`}>
                    {item.label}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* ══════════════════════════════════════════
            DESKTOP NAV BAR (inside header so it sticks)
        ══════════════════════════════════════════ */}
        {showNav && (
          <nav className="hidden md:block bg-[#131620] text-white border-t border-white/5"
            data-testid="desktop-nav">
            <div className="max-w-[1800px] mx-auto px-4">
              <div className="flex items-stretch h-11">
                {navItems.map((item, idx) => {
                  if (item.type === "mega-menu") {
                    return (
                      <div key={item.id}
                        className="relative flex-shrink-0"
                        onMouseEnter={openMega}
                        onMouseLeave={closeMega}>
                        <button
                          data-testid="nav-all-categories-btn"
                          onClick={() => setMegaOpen(v => !v)}
                          className="flex items-center gap-2 px-4 h-full text-sm font-bold text-white transition-all"
                          style={{ backgroundColor: brandColor }}>
                          <NavIcon name={item.icon_name} url={item.icon_url} className="w-4 h-4" />
                          {item.label}
                          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${megaOpen ? "rotate-180" : ""}`} />
                        </button>
                      </div>
                    );
                  }
                  return (
                    <span key={item.id} className="flex items-center">
                      {item.separator_before && <span className="w-px h-5 bg-slate-700 mx-1" />}
                      <Link
                        to={item.url || "/"}
                        className="flex items-center gap-1.5 px-3 h-full text-sm font-medium text-slate-300 hover:text-white hover:bg-white/5 transition-colors flex-shrink-0 whitespace-nowrap"
                      >
                        <NavIcon name={item.icon_name} url={item.icon_url} className="w-3.5 h-3.5" />
                        {item.label}
                        {item.badge && (
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded text-white ml-0.5"
                            style={{ backgroundColor: item.badge_color || "#2563eb" }}>
                            {item.badge}
                          </span>
                        )}
                      </Link>
                    </span>
                  );
                })}
              </div>
            </div>
          </nav>
        )}
      </header>

      {/* ══════════════════════════════════════════
          DESKTOP MEGA MENU DROPDOWN
      ══════════════════════════════════════════ */}
      {showNav && megaOpen && megaCfg.enabled !== false && (
        <div
          ref={megaRef}
          onMouseEnter={keepOpen}
          onMouseLeave={closeMega}
          className="hidden md:block fixed left-0 right-0 z-50 bg-white shadow-2xl border-t border-slate-100"
          style={{ top: `${megaTop}px` }}
          data-testid="mega-menu">
          <div className="max-w-[1800px] mx-auto flex" style={{ maxHeight: "520px" }}>

            {/* Left: Category list */}
            <div className="w-56 flex-shrink-0 bg-white border-r border-slate-100 overflow-y-auto py-2">
              {enabledCats.map(cat => (
                <div
                  key={cat.id}
                  onMouseEnter={() => setActiveCatId(cat.id)}
                  onClick={() => navigate(cat.url || "/")}
                  className="flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors group"
                  style={activeCat?.id === cat.id ? { backgroundColor: "#fff5eb" } : {}}
                  data-testid={`mega-cat-${cat.id}`}>
                  <span style={activeCat?.id === cat.id ? { color: brandColor } : { color: "#64748b" }}>
                    <NavIcon name={cat.icon_name} url={cat.icon_url} className="w-4 h-4" />
                  </span>
                  <span className={`text-sm font-medium ${activeCat?.id === cat.id ? "font-bold" : "text-slate-700"}`}
                    style={activeCat?.id === cat.id ? { color: brandColor } : {}}>
                    {cat.label}
                  </span>
                  <ChevronDown className="w-3 h-3 ml-auto -rotate-90 text-slate-400 opacity-50" />
                </div>
              ))}
            </div>

            {/* Middle: Sub-groups + Shop by category */}
            <div className="flex-1 overflow-y-auto p-6">
              {activeCat && (
                <>
                  {/* Sub-groups */}
                  {(activeCat.sub_groups || []).length > 0 && (
                    <div className="grid grid-cols-4 gap-6 mb-6">
                      {activeCat.sub_groups.map((group, gi) => (
                        <div key={gi}>
                          <p className="text-sm font-bold text-slate-800 mb-2">{group.title}</p>
                          <ul className="space-y-1">
                            {(group.items || []).map((item, ii) => (
                              <li key={ii}>
                                <Link
                                  to={item.url || `${activeCat.url || "/"}?q=${encodeURIComponent(item.label)}`}
                                  onClick={() => setMegaOpen(false)}
                                  className="text-sm text-slate-600 hover:text-orange-600 transition-colors">
                                  {item.label}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Shop by Category tiles */}
                  {megaCfg.shop_by_category?.enabled !== false && (megaCfg.shop_by_category?.items || []).length > 0 && (
                    <div>
                      <p className="text-sm font-bold text-slate-800 mb-3">
                        {megaCfg.shop_by_category.title || "Shop by Category"}
                      </p>
                      <div className="flex gap-3 flex-wrap">
                        {megaCfg.shop_by_category.items.map((tile, ti) => (
                          <Link
                            key={ti}
                            to={tile.url || "/"}
                            onClick={() => setMegaOpen(false)}
                            className="flex flex-col items-center gap-1.5 group">
                            <div className="w-16 h-16 rounded-xl overflow-hidden bg-slate-100 border border-slate-200 group-hover:border-orange-300 transition-colors">
                              {tile.image_url
                                ? <img src={tile.image_url} alt={tile.label} className="w-full h-full object-cover" />
                                : <div className="w-full h-full flex items-center justify-center text-slate-300 text-xs">{tile.label?.[0]}</div>
                              }
                            </div>
                            <span className="text-xs text-slate-600 group-hover:text-orange-600 transition-colors">{tile.label}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Fallback: show category link if no sub_groups */}
                  {(activeCat.sub_groups || []).length === 0 && (
                    <div className="flex items-center justify-center h-40">
                      <Link
                        to={activeCat.url || "/"}
                        onClick={() => setMegaOpen(false)}
                        className="text-sm font-semibold text-orange-600 hover:underline">
                        Browse all {activeCat.label} →
                      </Link>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Right: Creative banner */}
            {megaCfg.creative?.enabled !== false && (
              <div className="w-72 flex-shrink-0 p-4 flex items-stretch">
                <Link
                  to={megaCfg.creative.cta_url || "/"}
                  onClick={() => setMegaOpen(false)}
                  className="relative w-full rounded-2xl overflow-hidden flex flex-col justify-end bg-gradient-to-br from-orange-50 to-amber-100 hover:shadow-lg transition-shadow">
                  {megaCfg.creative.url && (
                    megaCfg.creative.type === "video" ? (
                      <video src={megaCfg.creative.url} autoPlay muted loop playsInline
                        className="absolute inset-0 w-full h-full object-cover" />
                    ) : (
                      <img src={megaCfg.creative.url} alt={megaCfg.creative.title}
                        className="absolute inset-0 w-full h-full object-cover" />
                    )
                  )}
                  <div className="relative z-10 p-5 bg-gradient-to-t from-black/50 to-transparent">
                    {megaCfg.creative.title && (
                      <p className="text-lg font-black text-white leading-tight">{megaCfg.creative.title}</p>
                    )}
                    {megaCfg.creative.subtitle && (
                      <p className="text-sm text-white/80 mt-0.5">{megaCfg.creative.subtitle}</p>
                    )}
                    {megaCfg.creative.cta_text && (
                      <span className="inline-block mt-3 px-4 py-2 rounded-full text-sm font-bold text-white"
                        style={{ backgroundColor: brandColor }}>
                        {megaCfg.creative.cta_text}
                      </span>
                    )}
                  </div>
                </Link>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════
          MOBILE DRAWER
      ══════════════════════════════════════════ */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />

          {/* Drawer */}
          <div className="relative w-80 max-w-[85vw] bg-[#0d0d1a] h-full overflow-y-auto shadow-2xl flex flex-col">
            {/* Drawer header */}
            <div className="flex items-center justify-between px-4 py-4 border-b border-white/10">
              {mobileCatId ? (
                <button onClick={() => setMobileCatId(null)} className="flex items-center gap-2 text-white">
                  <ArrowLeft className="w-4 h-4" />
                  <span className="text-sm font-semibold">All Categories</span>
                </button>
              ) : (
                <Link to="/" onClick={() => setMobileOpen(false)}>
                  <div className="flex flex-col leading-none">
                    <span className="text-lg font-black tracking-tight" style={{ color: brandColor }}>
                      IMPORT<span className="text-white">REND</span>
                    </span>
                    <span className="text-[7px] text-slate-400 font-bold tracking-[0.2em]">GLOBAL DEALS ——</span>
                  </div>
                </Link>
              )}
              <button onClick={() => setMobileOpen(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Category drill-down */}
            {mobileCatId ? (
              <MobileCategoryPanel
                cat={enabledCats.find(c => c.id === mobileCatId)}
                onClose={() => setMobileOpen(false)}
                brandColor={brandColor}
              />
            ) : (
              <div className="flex-1 overflow-y-auto">
                {/* Category list */}
                <div className="px-2 py-3">
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider px-2 mb-2">All Categories</p>
                  {enabledCats.map(cat => (
                    <button
                      key={cat.id}
                      onClick={() => setMobileCatId(cat.id)}
                      className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-white/5 transition-colors text-left">
                      <span className="text-slate-400">
                        <NavIcon name={cat.icon_name} url={cat.icon_url} className="w-4 h-4" />
                      </span>
                      <span className="flex-1 text-sm text-slate-200">{cat.label}</span>
                      <ChevronDown className="w-3.5 h-3.5 text-slate-500 -rotate-90" />
                    </button>
                  ))}
                </div>

                {/* Nav items */}
                <div className="px-2 py-3 border-t border-white/10">
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider px-2 mb-2">Quick Links</p>
                  {navItems.filter(i => i.type !== "mega-menu").map(item => (
                    <Link
                      key={item.id}
                      to={item.url || "/"}
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-white/5 transition-colors">
                      <span style={{ color: brandColor }}>
                        <NavIcon name={item.icon_name} url={item.icon_url} className="w-4 h-4" />
                      </span>
                      <span className="flex-1 text-sm text-slate-200">{item.label}</span>
                      {item.badge && (
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded text-white"
                          style={{ backgroundColor: item.badge_color || "#2563eb" }}>
                          {item.badge}
                        </span>
                      )}
                    </Link>
                  ))}
                </div>

                {/* CTA */}
                {ctaCfg.enabled !== false && (
                  <div className="px-4 pb-4 border-t border-white/10 pt-3">
                    <Link
                      to={ctaCfg.url || "/pages/import"}
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center justify-center gap-2 w-full py-3 rounded-full font-bold text-white text-sm"
                      style={{ backgroundColor: brandColor }}>
                      <Plane className="w-4 h-4" />
                      <Ship className="w-4 h-4" />
                      <span>{ctaCfg.text}</span>
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function MobileCategoryPanel({ cat, onClose, brandColor }) {
  const navigate = useNavigate();
  const hasSubGroups = (cat?.sub_groups || []).length > 0;

  useEffect(() => {
    if (cat && !hasSubGroups) {
      navigate(cat.url || "/");
      onClose();
    }
  }, [cat, hasSubGroups, navigate, onClose]);

  if (!cat || !hasSubGroups) return null;

  return (
    <div className="flex-1 overflow-y-auto px-2 py-3">
      <p className="text-xs font-bold text-orange-500 px-3 mb-3">{cat.label}</p>
      {cat.sub_groups.map((group, gi) => (
        <div key={gi} className="mb-4">
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider px-3 mb-1">{group.title}</p>
          {(group.items || []).map((item, ii) => (
            <Link
              key={ii}
              to={item.url || `${cat.url || "/"}?q=${encodeURIComponent(item.label)}`}
              onClick={onClose}
              className="flex items-center px-3 py-2.5 rounded-lg hover:bg-white/5 transition-colors">
              <span className="text-sm text-slate-300 hover:text-white">{item.label}</span>
            </Link>
          ))}
        </div>
      ))}
    </div>
  );
}
